// Copyright 2016 Sorama B.V.

import { bindable, customElement, inject, observable, ObserverLocator } from "aurelia-framework";
import { FrontEnd, Point3D } from "datatypes/SoramaLocalApi";
import { ChannelSelection } from "services/ChannelSelection/ChannelSelection";
import { IChannelSelectionPresetService } from "services/ChannelSelection/IChannelSelectionPresetService";
import { IHardwareService } from "services/Hardware/IHardwareService";

@customElement("channel-selector")
@inject("IHardwareService<FrontEnd>", IChannelSelectionPresetService, ObserverLocator)
export class ChannelSelector {
    // Injected dependencies
    private hardwareService: IHardwareService<FrontEnd>;
    private channelSelectionPresetService: IChannelSelectionPresetService;
    private observerLocator: ObserverLocator;

    // Public properties
    @bindable({ name: "selected-device-id", changeHandler: "selectedDeviceIdChanged" })
    public selectedDeviceId = "";

    private selectedDeviceIdChanged(value: string) {
        const devices = this.hardwareService.hardware;
        this.updateSelectedDevice(devices, []);
    }

    // Selected device state
    @observable
    protected selectedDevice: FrontEnd = null;

    // Channel grid state
    protected channelSelections: ChannelSelection[] = [];
    protected microphonePositions: Point3D[] = [];

    // Constructor et al.
    constructor(
        hardwareService: IHardwareService<FrontEnd>,
        channelSelectionPresetService: IChannelSelectionPresetService,
        observerLocator: ObserverLocator
    ) {
        this.hardwareService = hardwareService;
        this.channelSelectionPresetService = channelSelectionPresetService;
        this.observerLocator = observerLocator;
    }

    public attached(): void {
        // Subscribe to changes in the available devices, and update once manually
        this.observerLocator.getObserver(this.hardwareService, "hardware").subscribe(this.updateSelectedDevice);
    }

    public detached(): void {
        // Unsubscribe event
        this.observerLocator.getObserver(this.hardwareService, "hardware").unsubscribe(this.updateSelectedDevice);
    }

    // Helper functions for viewmodel
    private resetChannelSelection(): void {
        if (!this.selectedDevice) {
            this.channelSelections = [];
            return;
        }

        this.channelSelections = [this.channelSelectionPresetService.getDefaultPreset(this.selectedDevice)];
    }

    // 'Fat arrow' function so that 'this' is available from closure.
    private updateSelectedDevice = (devices: FrontEnd[], oldValue: FrontEnd[]): void => {
        if (
            (this.selectedDevice && this.selectedDeviceId == this.selectedDevice.id) ||
            (!this.selectedDevice && !this.selectedDeviceId)
        )
            return; // The device is already up to date

        // The selected device needs an update; check if the provided ID is valid
        this.selectedDevice = null;
        for (let i = 0; i < devices.length; i += 1) {
            if (this.selectedDeviceId == devices[i].id) {
                // It is; update the selected device
                this.selectedDevice = devices[i];
                break;
            }
        }

        this.microphonePositions = this.selectedDevice.microphonePositions.map(
            (x) => <Point3D>{ x: x.X, y: x.Y, z: x.Z }
        );
        this.resetChannelSelection();
    };
}
